import React, { memo, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { CalculateIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { TableBudget } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import AutoCalculateModal from './autoCalculateModal';
import messages from '../messages';
import useIntl from '../../../hooks/useIntl';
import {
  differencesToHighlight,
  getQueryStringValue,
  resolveActualDateLabel,
  resolveBudgetSections,
} from '../utils';
import type {
  BudgetType,
  SectionConfiguration,
} from 'containers/RentForecast/RDBudgetModal/types';

export const data: SectionConfiguration[] = [
  {
    header: 'Reserve Account',
    rows: [
      {
        label: '1. Beginning Balance',
        labelTooltip: <FormattedMessage {...messages.part3line1} />,
        autoCalculate: true,
      },
      {
        label: '2. Transfer to Reserve',
        labelTooltip: <FormattedMessage {...messages.part3line2} />,
      },
      {
        label: '\u00A0\u00A0\u00A0\u00A0' + 'Transfer from Reserve',
      },
      {
        label: '3. Operating Deficit',
      },
      {
        label: '4. Annual Capital Budget',
        labelSuffix: '(Part V - Reserve)',
        disabledWhen: {
          currentBudget: ['normal'],
          actual: ['normal'],
          proposedBudget: ['normal'],
        },
      },
      {
        label: '5. Building and Equipment Repair',
      },
      {
        label: '6. Other Non-Operating Expenses',
      },
      {
        label: '7. Total',
        labelSuffix: '(3 thru 6)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '8. Ending Balance',
        labelSuffix: '[(1+2)-7]',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'General Operating Account *',
    rows: [
      {
        label: 'Beginning Balance',
      },
      {
        label: 'Ending Balance',
      },
    ],
  },
  {
    header: 'Real Estate Tax and Insurance Escrow Accnt *',
    rows: [
      {
        label: 'Beginning Balance',
      },
      {
        label: 'Ending Balance',
      },
    ],
  },
  {
    header: 'Tenant Security Deposit Account *',
    rows: [
      {
        label: 'Beginning Balance',
      },
      {
        label: 'Ending Balance',
      },
      {
        label: '',
        labelSuffix: '(*Complete upon submission of actual expenses.)',
        isLabelOnly: true,
        stickyOnFirst: true,
      },
    ],
  },
  {
    // Rows from this area in the legacy version were in a separate table with a different field naming convention.
    // We have to follow the same field convention since there is already data stored with that convention.
    // Field naming contention is 2-bottom-{rowIndex}-{rowLabel}.
    // We have to apply custom style to the first row so it matches with the legacy appearance.
    header: null,
    headless: true,
    rows: [
      {
        label: 'Number of Applicants on the Waiting List',
        placeholder: '0',
        emptyValue: 0,
        overrideKeys: {
          currentBudget: '2-bottom-0-Number of Applicants on the Waiting List',
        },
        overrideTypes: { currentBudget: 'number' },
        skipKeys: ['actual', 'intacctActual', 'proposedBudget', 'comments'],
        sx: { borderTop: '1px solid #D1D1D1' },
        dynamicForceEnableKeys: ({ budgetType }) => {
          return budgetType === 'normal' || budgetType === 'custom'
            ? ['currentBudget']
            : [];
        },
      },
      {
        label: 'Number of Applicants Needing RA',
        placeholder: '0',
        emptyValue: 0,
        overrideKeys: {
          currentBudget: '2-bottom-1-Number of Applicants Needing RA',
        },
        overrideTypes: { currentBudget: 'number' },
        skipKeys: ['actual', 'intacctActual', 'proposedBudget', 'comments'],
        dynamicForceEnableKeys: ({ budgetType }) => {
          return budgetType === 'normal' || budgetType === 'custom'
            ? ['currentBudget']
            : [];
        },
      },
      {
        label: 'Reserve Account Required Balance',
        placeholder: '0.00',
        emptyValue: 0,
        overrideKeys: {
          currentBudget: '2-bottom-2-Reserve Account Required Balance',
        },
        skipKeys: ['actual', 'intacctActual', 'proposedBudget', 'comments'],
        dynamicTooltip: ({ parameters }) => {
          if (parameters.reserveAutoFilled) {
            return (
              'This value is pulled from the property settings by default. ' +
              'You can edit it here without changing the property value.'
            );
          }

          return null;
        },
        dynamicForceEnableKeys: ({ budgetType }) => {
          return budgetType === 'normal' || budgetType === 'custom'
            ? ['currentBudget']
            : [];
        },
      },
      {
        label: 'Amount Ahead / Behind',
        labelTooltip:
          'Amount Ahead/Behind = Reserve Account Required - Line 8 Ending Balance Proposed',
        placeholder: '0.00',
        emptyValue: 0,
        overrideKeys: { currentBudget: '2-bottom-3-Amount Ahead / Behind' },
        skipKeys: ['actual', 'intacctActual', 'proposedBudget', 'comments'],
        disabledWhen: {
          currentBudget: ['custom'],
        },
      },
      {
        label: 'Insurance Deductible',
        placeholder: '0.00',
        emptyValue: 0,
        overrideKeys: { currentBudget: '2-bottom-4-Insurance Deductible' },
        skipKeys: ['actual', 'intacctActual', 'proposedBudget', 'comments'],
        dynamicForceEnableKeys: ({ budgetType }) => {
          return budgetType === 'normal' || budgetType === 'custom'
            ? ['currentBudget']
            : [];
        },
      },
    ],
  },
];

export const generateHeaders = (
  year,
  actualDateRange,
  intacctDifferenceCount,
  enableAutoCalculate,
  budgetType: BudgetType,
  setOpenAutoCalculate,
  rentForecastCreatedDate: string,
  disableIntacctActuals: boolean = false,
) => [
  {
    label: 'Modified After Intacct sync',
    subLabel: `${intacctDifferenceCount} total items across Parts I, II, III, and V`,
    legend: { backgroundColor: 'warning.lighter' },
    flexSize: 1.5,
    textAlign: 'left',
    hidden: budgetType === 'custom',
    hideHeader: budgetType === 'custom',
  },
  {
    label: `${budgetType === 'approved' ? 'Approved' : 'Current'} Budget`,
    subLabel: `01/01/${year} - 12/31/${year}`,
    key: 'currentBudget',
    flexSize: 1,
    emptyValue: 0,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'normal' ||
      budgetType === 'approved',
  },
  {
    label: 'Actual',
    subLabel: resolveActualDateLabel(year, actualDateRange),
    key: 'actual',
    flexSize: 1,
    emptyValue: 0,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
  {
    label: 'Proposed Budget',
    subLabel: `01/01/${parseInt(year) + 1} - 12/31/${parseInt(year) + 1}`,
    key: 'proposedBudget',
    flexSize: 1,
    emptyValue: 0,
    placeholder: '0.00',
    type: 'number-currency',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
    ...(budgetType === 'normal'
      ? {
          action: {
            label: 'Auto-Calculate',
            startIcon: <CalculateIcon />,
            onClick: () => setOpenAutoCalculate(true),
          },
        }
      : {}),
  },
  ...(budgetType?.startsWith('custom')
    ? [
        {
          label: 'YTD Actuals',
          subLabel: resolveActualDateLabel(year, {
            from: moment(rentForecastCreatedDate).startOf('year'),
            to: moment(rentForecastCreatedDate),
          }),
          key: 'intacctActual',
          flexSize: 1,
          emptyValue: 0,
          type: 'number-currency',
          disabled: budgetType === 'custom-disabled' || disableIntacctActuals,
        },
      ]
    : []),
  {
    label: 'Comments',
    key: 'comments',
    textAlign: 'left',
    flexSize: 1,
    placeholder: 'Comment',
    type: 'text',
    disabled:
      budgetType === 'disabled' ||
      budgetType === 'custom-disabled' ||
      budgetType === 'approved',
  },
];

const Part3 = ({
  useFormReturn,
  year,
  intacctDifferences,
  reserveAutoFilled,
  enableAutoCalculate,
  actualDateRange,
  rentForecast,
  budgetType,
}) => {
  const intl = useIntl();
  const [openAutoCalculate, setOpenAutoCalculate] = useState(false);
  const [highlightFields, differenceCount] = useMemo(
    () => differencesToHighlight(intacctDifferences),
    [intacctDifferences],
  );

  const runAutoCalculate = () => {
    const actualEndingBalance = parseFloat(
      useFormReturn.getValues('2-0-8-actual') || 0,
    );
    const transferToReserveBudget = parseFloat(
      useFormReturn.getValues('2-0-1-currentBudget') || 0,
    );

    const autoCalculatedValue =
      actualEndingBalance + (transferToReserveBudget / 12) * 6;
    const formattedValue = (
      Math.round(autoCalculatedValue * 100) / 100
    ).toFixed(2);
    useFormReturn.setValue('2-0-0-proposedBudget', formattedValue, {
      shouldValidate: true,
      shouldDirty: true,
    });
    useFormReturn.setValue(
      '2-0-1-proposedBudget',
      transferToReserveBudget.toFixed(2),
    );
  };

  const handleAutoCalculateSubmit = () => {
    runAutoCalculate();
    setOpenAutoCalculate(false);
    toastr.success(
      intl.formatMessage(messages.success),
      intl.formatMessage(messages.autoCalculationPart3Success),
    );
  };

  const sections = useMemo(
    () =>
      resolveBudgetSections(data, budgetType, {
        parameters: {
          reserveAutoFilled,
        },
      }),
    [rentForecast],
  );

  const headers = generateHeaders(
    year,
    actualDateRange,
    differenceCount,
    enableAutoCalculate,
    budgetType,
    setOpenAutoCalculate,
    rentForecast?.createdAt,
    !rentForecast?.parameters?.intacctYTDActuals,
  );

  const renderAll = getQueryStringValue('fullRenderTable') === 'true';

  return (
    <>
      <AutoCalculateModal
        open={openAutoCalculate}
        closeModal={() => setOpenAutoCalculate(false)}
        title={intl.formatMessage(messages.autoCalculationPart3ModalHeader)}
        body={intl.formatMessage(messages.autoCalculationPart3ModalBody)}
        warningText={intl.formatMessage(
          messages.autoCalculationPart3ModalWarning,
        )}
        onSubmit={handleAutoCalculateSubmit}
      />
      <TableBudget
        fieldNamePrefix={'2'}
        columns={headers}
        sections={sections}
        useFormReturn={useFormReturn}
        tableBodySize={{ height: 800, width: 1488 }}
        highlightFields={highlightFields}
        renderAll={renderAll}
      />
    </>
  );
};

export default memo(Part3);
